import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  appInsights: ApplicationInsights;

  constructor() {
    try {
      // Check if we have either connection string or instrumentation key
      const hasConnectionString = environment.appInsights.connectionString && environment.appInsights.connectionString.trim() !== '';
      const hasInstrumentationKey = environment.appInsights.instrumentationKey && environment.appInsights.instrumentationKey.trim() !== '';
      
      if (!hasConnectionString && !hasInstrumentationKey) {
        return;
      }
      
      const config: any = {
        enableAutoRouteTracking: true,
        enableCorsCorrelation: false,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        disableExceptionTracking: false,
        enableUnhandledPromiseRejectionTracking: true
      };
      
      // Prefer connection string over instrumentation key (modern approach)
      if (hasConnectionString) {
        config.connectionString = environment.appInsights.connectionString;
      } else {
        config.instrumentationKey = environment.appInsights.instrumentationKey;
      }
      
      this.appInsights = new ApplicationInsights({
        config: config
      });
      
      this.appInsights.loadAppInsights();
      
      // Test immediate connection
      this.appInsights.trackEvent({ name: 'LoggingService_Initialized' });
      
    } catch (error) {
      // Silent error handling
    }
  }

  logPageView(name?: string, url?: string) { // option to call manually
    this.appInsights.trackPageView({
      name: name,
      uri: url
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    try {
      if (!this.appInsights) {
        return;
      }
      this.appInsights.trackEvent({ name: name }, properties);
    } catch (error) {
      // Silent error handling
    }
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.appInsights.trackMetric({ name: name, average: average }, properties);
  }

  // Helper method to get browser information using modern APIs
  private getBrowserInfo(): { name: string, version: string, userAgent: string } {
    const userAgent = navigator.userAgent;
    
    // Use navigator.userAgentData if available (modern browsers)
    if ('userAgentData' in navigator && (navigator as any).userAgentData) {
      const uaData = (navigator as any).userAgentData;
      const brand = uaData.brands?.find((brand: any) => 
        !brand.brand.includes('Not') && !brand.brand.includes('Chromium')
      );
      
      return {
        name: brand?.brand || 'Unknown',
        version: brand?.version || 'Unknown',
        userAgent: userAgent
      };
    }
    
    // Fallback to userAgent parsing for older browsers
    let browserName = 'Unknown';
    let browserVersion = 'Unknown';

    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      browserName = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Edg')) {
      browserName = 'Edge';
      const match = userAgent.match(/Edg\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browserName = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    }

    return {
      name: browserName,
      version: browserVersion,
      userAgent: userAgent
    };
  }

  // Helper method to get performance metrics
  private getPerformanceInfo(): any {
    const performance = window.performance;
    const timing = performance.timing;
    const navigation = performance.navigation;
    
    return {
      // Page load performance
      pageLoadTime: timing.loadEventEnd - timing.navigationStart,
      domReadyTime: timing.domContentLoadedEventEnd - timing.navigationStart,
      firstPaintTime: performance.getEntriesByType('paint').find(entry => entry.name === 'first-paint')?.startTime || 0,
      firstContentfulPaint: performance.getEntriesByType('paint').find(entry => entry.name === 'first-contentful-paint')?.startTime || 0,
      
      // Memory usage (if available)
      memoryUsed: (performance as any).memory?.usedJSHeapSize || 0,
      memoryTotal: (performance as any).memory?.totalJSHeapSize || 0,
      memoryLimit: (performance as any).memory?.jsHeapSizeLimit || 0,
      
      // Navigation info
      navigationType: navigation.type,
      redirectCount: navigation.redirectCount,
      
      // Current performance
      currentTime: performance.now()
    };
  }
  
  // Helper method to get DOM and page state
  private getDOMState(): any {
    return {
      // Document state
      readyState: document.readyState,
      title: document.title,
      url: window.location.href,
      referrer: document.referrer,
      domain: document.domain,
      
      // Viewport and scroll
      scrollX: window.scrollX || window.pageXOffset,
      scrollY: window.scrollY || window.pageYOffset,
      documentHeight: document.documentElement.scrollHeight,
      documentWidth: document.documentElement.scrollWidth,
      
      // Focus and visibility
      hasFocus: document.hasFocus(),
      visibilityState: document.visibilityState,
      activeElement: document.activeElement?.tagName || 'Unknown',
      activeElementId: document.activeElement?.id || '',
      
      // Form state (if any forms are present)
      formsCount: document.forms.length,
      inputsCount: document.querySelectorAll('input, select, textarea').length
    };
  }
  
  // Helper method to get Angular-specific context
  private getAngularContext(): any {
    try {
      return {
        // Angular version (if available in global)
        angularVersion: (window as any).ng?.version?.full || 'Unknown',
        
        // Route information
        currentRoute: window.location.pathname,
        routeParams: window.location.search,
        routeHash: window.location.hash,
        
        // Local storage info (keys only for privacy)
        localStorageKeys: Object.keys(localStorage),
        sessionStorageKeys: Object.keys(sessionStorage),
        
        // Component lifecycle (can be enhanced per component)
        componentContext: 'LoggingService',
        
        // Error boundaries and zones
        zoneJsPresent: typeof Zone !== 'undefined'
      };
    } catch (error) {
      return {
        error: 'Failed to get Angular context',
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  // Helper method to get network and connectivity info
  private getNetworkInfo(): any {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    return {
      // Network connection
      online: navigator.onLine,
      connectionType: connection?.effectiveType || 'Unknown',
      downlink: connection?.downlink || 'Unknown',
      rtt: connection?.rtt || 'Unknown', // Round trip time
      saveData: connection?.saveData || false,
      
      // Geolocation (availability only, not actual location for privacy)
      geolocationAvailable: 'geolocation' in navigator,
      
      // Service worker
      serviceWorkerAvailable: 'serviceWorker' in navigator,
      serviceWorkerController: navigator.serviceWorker?.controller ? 'Active' : 'None',
      
      // Cookies enabled
      cookieEnabled: navigator.cookieEnabled
    };
  }
  
  // Helper method to get error context and stack trace info
  private getErrorContext(error?: any): any {
    const errorInfo: any = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };
    
    if (error) {
      errorInfo.errorName = error.name || 'Unknown';
      errorInfo.errorMessage = error.message || 'No message';
      errorInfo.errorStack = error.stack || 'No stack trace';
      errorInfo.errorFileName = error.fileName || 'Unknown';
      errorInfo.errorLineNumber = error.lineNumber || 0;
      errorInfo.errorColumnNumber = error.columnNumber || 0;
      
      // Parse stack trace for better debugging
      if (error.stack) {
        const stackLines = error.stack.split('\n');
        errorInfo.stackFrames = stackLines.slice(0, 10).map((line: string, index: number) => ({
          frame: index,
          line: line.trim()
        }));
      }
    }
    
    return errorInfo;
  }
  
  // Helper method to get system information using modern APIs
  private getSystemInfo(): { platform: string, architecture?: string, mobile: boolean, touchSupport: boolean } {
    // Use navigator.platform and other modern APIs
    const platform = navigator.platform || 'Unknown';
    const mobile = 'maxTouchPoints' in navigator ? navigator.maxTouchPoints > 0 : false;
    const touchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Get architecture if available
    let architecture: string | undefined;
    if ('userAgentData' in navigator && (navigator as any).userAgentData) {
      architecture = (navigator as any).userAgentData.platform;
    }
    
    return {
      platform: platform,
      architecture: architecture,
      mobile: mobile,
      touchSupport: touchSupport
    };
  }

  // Helper method to get user information
  private getUserInfo(): { userId: string, userName: string, email: string } {
    return {
      userId: localStorage.getItem('_loginId') || 'Unknown',
      userName: localStorage.getItem('_userName') || 'Unknown',
      email: localStorage.getItem('_emailId') || 'Unknown'
    };
  }

  // Helper method to create enhanced properties for logging
  private createEnhancedProperties(additionalProperties?: { [key: string]: any }, includeErrorContext?: any): { [key: string]: any } {
    const browserInfo = this.getBrowserInfo();
    const userInfo = this.getUserInfo();
    const systemInfo = this.getSystemInfo();
    const performanceInfo = this.getPerformanceInfo();
    const domState = this.getDOMState();
    const angularContext = this.getAngularContext();
    const networkInfo = this.getNetworkInfo();
    const errorContext = includeErrorContext ? this.getErrorContext(includeErrorContext) : {};
    
    const enhancedProperties = {
      // === BROWSER & SYSTEM INFO ===
      browserName: browserInfo.name,
      browserVersion: browserInfo.version,
      userAgent: browserInfo.userAgent,
      platform: systemInfo.platform,
      architecture: systemInfo.architecture || 'Unknown',
      deviceType: systemInfo.mobile ? 'Mobile' : 'Desktop',
      touchSupport: systemInfo.touchSupport,
      language: navigator.language,
      languages: navigator.languages?.join(', ') || navigator.language,
      hardwareConcurrency: navigator.hardwareConcurrency || 'Unknown',
      deviceMemory: (navigator as any).deviceMemory || 'Unknown',
      
      // === USER CONTEXT ===
      userId: userInfo.userId,
      userName: userInfo.userName,
      userEmail: userInfo.email,
      sessionTimestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      
      // === PERFORMANCE METRICS ===
      pageLoadTime: performanceInfo.pageLoadTime,
      domReadyTime: performanceInfo.domReadyTime,
      firstPaintTime: performanceInfo.firstPaintTime,
      firstContentfulPaint: performanceInfo.firstContentfulPaint,
      memoryUsed: performanceInfo.memoryUsed,
      memoryTotal: performanceInfo.memoryTotal,
      memoryLimit: performanceInfo.memoryLimit,
      currentPerformanceTime: performanceInfo.currentTime,
      
      // === SCREEN & VIEWPORT ===
      screenResolution: `${screen.width}x${screen.height}`,
      screenColorDepth: screen.colorDepth,
      screenPixelDepth: screen.pixelDepth,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      screenOrientation: (screen as any).orientation?.type || 'Unknown',
      
      // === PAGE & DOM STATE ===
      documentReadyState: domState.readyState,
      pageTitle: domState.title,
      currentUrl: domState.url,
      referrer: domState.referrer,
      scrollPosition: `${domState.scrollX},${domState.scrollY}`,
      documentSize: `${domState.documentWidth}x${domState.documentHeight}`,
      hasFocus: domState.hasFocus,
      visibilityState: domState.visibilityState,
      activeElement: domState.activeElement,
      activeElementId: domState.activeElementId,
      formsCount: domState.formsCount,
      inputsCount: domState.inputsCount,
      
      // === ANGULAR CONTEXT ===
      angularVersion: angularContext.angularVersion,
      currentRoute: angularContext.currentRoute,
      routeParams: angularContext.routeParams,
      routeHash: angularContext.routeHash,
      localStorageKeysCount: angularContext.localStorageKeys?.length || 0,
      sessionStorageKeysCount: angularContext.sessionStorageKeys?.length || 0,
      zoneJsPresent: angularContext.zoneJsPresent,
      
      // === NETWORK & CONNECTIVITY ===
      isOnline: networkInfo.online,
      connectionType: networkInfo.connectionType,
      connectionDownlink: networkInfo.downlink,
      connectionRtt: networkInfo.rtt,
      dataSaverMode: networkInfo.saveData,
      geolocationAvailable: networkInfo.geolocationAvailable,
      serviceWorkerAvailable: networkInfo.serviceWorkerAvailable,
      serviceWorkerController: networkInfo.serviceWorkerController,
      cookieEnabled: networkInfo.cookieEnabled,
      
      // === ERROR CONTEXT (if provided) ===
      ...errorContext,
      
      // === DEBUGGING AIDS ===
      logLevel: 'Enhanced',
      debuggingEnabled: true,
      captureTime: Date.now(),
      
      // Additional properties if provided
      ...additionalProperties
    };
    
    return enhancedProperties;
  }

  logException(exception: Error, severityLevel?: number, additionalProperties?: { [key: string]: any }) {
    try {
      if (!this.appInsights) {
        return;
      }
      
      const enhancedProperties = this.createEnhancedProperties({
        errorType: 'Exception',
        errorMessage: exception.message,
        errorStack: exception.stack,
        errorName: exception.name,
        severityLevel: severityLevel?.toString() || '0',
        // Add component/feature context if available
        componentName: additionalProperties?.componentName || 'Unknown',
        featureName: additionalProperties?.featureName || 'Unknown',
        userAction: additionalProperties?.userAction || 'Unknown',
        ...additionalProperties
      }, exception);
      
      this.appInsights.trackException({ 
        exception: exception, 
        severityLevel: severityLevel,
        properties: enhancedProperties
      });
    } catch (error) {
      // Silent error handling
    }
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    try {
      if (!this.appInsights) {
        return;
      }
      
      const enhancedProperties = this.createEnhancedProperties({
        traceMessage: message,
        traceLevel: properties?.level || 'Info',
        eventType: 'Trace',
        ...properties
      });
      
      this.appInsights.trackTrace({ message: message }, enhancedProperties);
    } catch (error) {
      // Silent error handling
    }
  }

  setUserId(userId: string) {
    this.appInsights.setAuthenticatedUserContext(userId);
  }

  clearUserId() {
    this.appInsights.clearAuthenticatedUserContext();
  }

  flush() {
    this.appInsights.flush();
  }

  // Test method to verify Application Insights is working
  testLogging() {
    try {
      this.logEvent('TestEvent', { testProperty: 'testValue', timestamp: new Date().toISOString() });
      this.logTrace('Test trace message from LoggingService');
      
      // Force send the telemetry
      this.flush();
    } catch (error) {
      // Silent error handling
    }
  }

  // Force send all pending telemetry
  forceSend() {
    if (this.appInsights) {
      this.appInsights.flush();
    }
  }

  // Enhanced error logging method with full context
  logError(errorMessage: string, errorDetails?: any, additionalProperties?: { [key: string]: any }) {
    try {
      if (!this.appInsights) {
        return;
      }
      
      const enhancedProperties = this.createEnhancedProperties({
        errorMessage: errorMessage,
        errorDetails: errorDetails ? JSON.stringify(errorDetails) : undefined,
        errorType: 'Application Error',
        ...additionalProperties
      }, errorDetails);
      
      // Log as both an event and a trace for better visibility
      this.appInsights.trackEvent({ name: 'Application_Error' }, enhancedProperties);
      this.appInsights.trackTrace({ message: `Error: ${errorMessage}` }, enhancedProperties);
      
    } catch (error) {
      // Silent error handling
    }
  }
  
  // Log user interactions for debugging user flows
  logUserAction(action: string, elementInfo?: any, additionalProperties?: { [key: string]: any }) {
    try {
      if (!this.appInsights) {
        return;
      }
      
      const enhancedProperties = this.createEnhancedProperties({
        userAction: action,
        elementType: elementInfo?.tagName || 'Unknown',
        elementId: elementInfo?.id || '',
        elementClass: elementInfo?.className || '',
        elementText: elementInfo?.textContent?.substring(0, 100) || '',
        clickCoordinates: elementInfo?.coordinates || 'Unknown',
        eventType: 'UserAction',
        ...additionalProperties
      });
      
      this.appInsights.trackEvent({ name: 'User_Action' }, enhancedProperties);
    } catch (error) {
      // Silent error handling
    }
  }
  
  // Log API call attempts and results for debugging
  logApiAttempt(method: string, url: string, requestData?: any, additionalProperties?: { [key: string]: any }) {
    try {
      if (!this.appInsights) {
        return;
      }
      
      const enhancedProperties = this.createEnhancedProperties({
        apiMethod: method,
        apiUrl: url,
        requestSize: requestData ? JSON.stringify(requestData).length : 0,
        hasRequestData: !!requestData,
        eventType: 'API_Attempt',
        ...additionalProperties
      });
      
      this.appInsights.trackEvent({ name: 'API_Attempt' }, enhancedProperties);
    } catch (error) {
      // Silent error handling
    }
  }
  
  // Log component lifecycle events for debugging
  logComponentLifecycle(componentName: string, lifecycle: string, additionalProperties?: { [key: string]: any }) {
    try {
      if (!this.appInsights) {
        return;
      }
      
      const enhancedProperties = this.createEnhancedProperties({
        componentName: componentName,
        lifecycleEvent: lifecycle,
        eventType: 'Component_Lifecycle',
        ...additionalProperties
      });
      
      this.appInsights.trackEvent({ name: 'Component_Lifecycle' }, enhancedProperties);
    } catch (error) {
      // Silent error handling
    }
  }
  
  // Log form validation errors with field context
  logFormValidationError(formName: string, fieldName: string, validationError: string, fieldValue?: any, additionalProperties?: { [key: string]: any }) {
    try {
      if (!this.appInsights) {
        return;
      }
      
      const enhancedProperties = this.createEnhancedProperties({
        formName: formName,
        fieldName: fieldName,
        validationError: validationError,
        fieldValueLength: fieldValue?.toString().length || 0,
        fieldValueType: typeof fieldValue,
        eventType: 'Form_Validation_Error',
        ...additionalProperties
      });
      
      this.appInsights.trackEvent({ name: 'Form_Validation_Error' }, enhancedProperties);
    } catch (error) {
      // Silent error handling
    }
  }
}